import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/hooks/useAuth';
import { interviewRuntimeAPI } from '../api/interviewRuntimeAPI';
import { useSpeechPlayback } from '../hooks/useSpeechPlayback';
import { AVATAR_PROVIDERS, avatarTrialProvider, useAvatar } from '../avatar/useAvatar';
import { useVoiceRecording } from '../hooks/useVoiceRecording';
import InterviewAvatarStage from './InterviewAvatarStage';
import StageDivider, { loadStageHeight, saveStageHeight } from './StageDivider';
import InterviewBoard from './InterviewBoard';
import { timingText } from '../timing';
import { PromptLine } from './RichText';
import InterviewUpgradeGate, { isUpgradeRequiredError, refusalSentence, useGateStatus } from './InterviewUpgradeGate';
import { holdsPaidExam } from '../../quiz-generation/components/LockedPartNotice';
import { withAppBasename } from '../../../config/appBase';
import {
  clearSittingState, isNoticeId, liveSlot, loadSittingState, NOTICE_PREFIX, saveSittingState,
} from '../sittingStorage';
import './InterviewSitting.css';

// The runtime says nothing when a question ends (`say` is empty on the turn
// that completes it). Without these the next question arrived with no seam at
// all, and the last answer of the sitting jumped straight to the report.
export const NEXT_QUESTION_LEAD = 'Thank you. Let us move on to something different.';
export const CLOSING_LINE = 'Thank you. That is the end of this interview.';

// A soft time for each question, from how long the whole interview lasts
// where that is known: an Oxbridge interview is about 30 minutes (Oxford PPE
// 20); a Medicine or Dentistry interview runs its school's time per station
// (the course overview works it out from the timing audit).
export function liveTargetSeconds(minutes, liveQuestions) {
  return minutes && liveQuestions ? Math.round((minutes * 60) / liveQuestions) : null;
}

/** duration_minutes / sessions / runnable-questions-in-this-session, the
 * same derivation InterviewPaperBriefPage uses - kept in sync deliberately
 * rather than trusting a value passed through route state that a reload
 * would lose anyway. */
/**
 * Where a live interview stands from the runs the server holds for it.
 *
 * Claimed interview (a Beginnings-only account's one free mock): every run
 * counts, so a completed station is skipped, an open one is resumed with its
 * run id and only a station with no run is started (a POST /runs on a
 * question that already has a run is refused). Any other account: only open
 * runs count, and not at all on a fresh Start from the course page (which
 * means "start again"), so a retake still begins new runs.
 *
 * Returns { runByQuestion, currentIndex, complete }.
 */
export function mergeServerRuns(stations, serverRuns, freshStart) {
  const none = { runByQuestion: {}, currentIndex: 0, complete: false };
  if (!serverRuns || (freshStart && !serverRuns.claimed)) return none;
  const runByQuestion = {};
  let currentIndex = -1;
  let held = 0;
  stations.forEach((st, i) => {
    if (!st.live) return;
    const run = serverRuns.runs[st.question_id];
    if (!run?.run_id) return;
    if (!serverRuns.claimed && run.is_complete !== false) return;
    runByQuestion[st.question_id] = run.run_id;
    held += 1;
    if (currentIndex < 0 && run.is_complete === false) currentIndex = i;
  });
  if (!held) return none;
  if (currentIndex >= 0) return { runByQuestion, currentIndex, complete: false };
  // No open run. The next station to start is the first live one with no run.
  const next = stations.findIndex((st) => st.live && !runByQuestion[st.question_id]);
  if (next >= 0) return { runByQuestion, currentIndex: next, complete: false };
  return { runByQuestion, currentIndex: stations.length - 1, complete: true };
}

// How long a start screen with a connected (billing) face waits for Begin.
export const AUTO_BEGIN_SECONDS = 30;

/** Whether the page has had a tap or key press yet. Browsers that can't say
 * are taken to have had one: they predate the rule that needs it. */
function hasHadTap() {
  const activation = typeof navigator !== 'undefined' ? navigator.userActivation : undefined;
  return activation ? activation.hasBeenActive : true;
}

/** One interviewer, at random, from the pool the course gives an avatar
 * interview: {id, voice}, or null when it gives none. */
export function pickExaminer(examiners) {
  const pool = (examiners || []).filter((e) => e && e.id && e.voice);
  return pool.length ? pool[Math.floor(Math.random() * pool.length)] : null;
}

/** A voice, at random, for an interview with no avatar (one with an avatar
 * speaks with its interviewer's own). null when the course gives none: the
 * server's default then. */
export function pickVoice(voices) {
  const pool = (voices || []).filter(Boolean);
  return pool.length ? pool[Math.floor(Math.random() * pool.length)] : null;
}

function deriveSoftTargetSeconds(paper, questionIds) {
  if (!paper) return null;
  const totalSessions = (paper.sessions || []).length || 1;
  const minutes = (paper.metadata.duration_minutes || 60) / totalSessions / (questionIds.length || 1);
  return Math.round(minutes * 60);
}

/**
 * The spoken interview. Two ways in: a live interview for a course (the
 * route has no paper; the stations arrive from "Start live interview" and
 * are kept on this device), or a single paper's session, kept for sittings
 * started before mock interview papers became written.
 *
 * The sitting is saved under the signed-in user's id, so nothing is resolved
 * until that id is known: without it a reload would find nothing saved and
 * start afresh over the student's real sitting. Keyed on the id, so a sitting
 * held in memory never carries over to another account.
 */
export default function InterviewSittingPage() {
  const { user } = useAuth();
  const userId = user?.id || null;
  if (!userId) return <div className="is-loading">Loading your interview…</div>;
  return <InterviewSitting key={userId} userId={userId} paid={holdsPaidExam(user)} />;
}

function InterviewSitting({ userId, paid }) {
  const params = useParams();
  const { workspace: workspaceSlug, leafKey } = params;
  const isLive = Boolean(params.setId);
  const [paperId, sessionId] = isLive ? liveSlot(leafKey, params.setId) : [params.paperId, params.sessionId];
  const courseUrl = `/interviews/${workspaceSlug}/${leafKey}`;
  const location = useLocation();
  const navigate = useNavigate();

  // Mobile browsers won't play audio until a real tap has happened on the
  // page - gates the auto-speak effect below until that tap fires.
  const [started, setStarted] = useState(false);
  const [sittingComplete, setSittingComplete] = useState(false);
  // A lip-synced examiner, live interviews only: for browsers opted into the
  // trial (?avatar=simli or ?avatar=liveavatar), or on an interview the course
  // pins one to for this account (set once the sitting is resolved, below).
  const [avatarProvider, setAvatarProvider] = useState(() => (isLive ? avatarTrialProvider(location.search) : null));
  // The interviewer picked for this sitting (face and voice), saved with it so
  // a reload brings back the same person.
  const [examiner, setExaminer] = useState(null);
  // The voice an interview without an avatar picked, kept with it likewise.
  const [plainVoice, setPlainVoice] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | error | upgrade
  const [gateCode, statusFor] = useGateStatus();
  // The face connects on the start screen, so it is ready by Begin, and stays
  // until the last answer: the provider bills connected time, so the start
  // screen then begins by itself after AUTO_BEGIN_SECONDS. Only where the
  // page has had a tap already (the course page's Start): a face and voice
  // that start with no tap at all would be refused sound.
  const preconnect = isLive && Boolean(avatarProvider) && status === 'ready' && !started && hasHadTap();
  const avatar = useAvatar(avatarProvider, (started || preconnect) && !sittingComplete, examiner?.id ?? null);
  const {
    speak, prefetch, stop: stopSpeaking, unlock: unlockSpeech, isSpeaking, speakingPlainly, getAmplitude,
  } = useSpeechPlayback(avatarProvider ? avatar : null, (avatarProvider ? examiner?.voice : plainVoice) ?? null);
  const voice = useVoiceRecording();
  const [autoBeginIn, setAutoBeginIn] = useState(AUTO_BEGIN_SECONDS);
  // The examiner's height on a wide screen, if the student has dragged it
  // (StageDivider); null leaves it to the layout.
  const [stageHeight, setStageHeight] = useState(loadStageHeight);
  const resizeStage = useCallback((h) => { setStageHeight(h); saveStageHeight(h); }, []);
  const leftRef = useRef(null);
  const captionRef = useRef(null);
  // The question runs on below its box (a wide screen, a long question).
  const [captionMore, setCaptionMore] = useState(false);
  const checkCaption = useCallback(() => {
    const el = captionRef.current;
    setCaptionMore(Boolean(el) && el.scrollHeight - el.scrollTop - el.clientHeight > 8);
  }, []);
  const [paper, setPaper] = useState(null);
  const [questionIds, setQuestionIds] = useState([]);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [runId, setRunId] = useState(null);
  const [transcript, setTranscript] = useState([]);
  const [say, setSay] = useState('');
  const [rungId, setRungId] = useState(null);
  const [expects, setExpects] = useState({ sketch: false, computation: false });
  const [questionExhibit, setQuestionExhibit] = useState(null);
  const [rungExhibit, setRungExhibit] = useState(null);
  const [answerText, setAnswerText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [turnError, setTurnError] = useState('');
  const [sketchBusy, setSketchBusy] = useState(false);
  const [sketchResult, setSketchResult] = useState(null);
  const [computationBusy, setComputationBusy] = useState(false);
  const [computationResult, setComputationResult] = useState(null);
  const [cameraEnabled, setCameraEnabled] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [finishedRunIds, setFinishedRunIds] = useState([]);
  const [stations, setStations] = useState(null);
  const [liveLeaf, setLiveLeaf] = useState(null);
  const [liveMinutes, setLiveMinutes] = useState(null);
  const [liveTiming, setLiveTiming] = useState(null);

  const spokenTurnCountRef = useRef(0);
  const elapsedTickRef = useRef(null);

  const softTargetSeconds = useMemo(() => {
    if (isLive) return liveTargetSeconds(liveMinutes, questionIds.filter((q) => !isNoticeId(q)).length);
    if (['Medicine', 'Dentistry'].includes(paper?.metadata?.subject_name)) return null;
    return deriveSoftTargetSeconds(paper, questionIds);
  }, [isLive, liveMinutes, paper, questionIds]);

  // Progress is saved merged, so a live interview's stations and course
  // travel with every save rather than being dropped by the first advance.
  const persist = useCallback((patch) => {
    saveSittingState(userId, paperId, sessionId, { ...(loadSittingState(userId, paperId, sessionId) || {}), ...patch });
  }, [userId, paperId, sessionId]);

  const onNotice = isNoticeId(questionIds[questionIndex]);

  // ---- boot: resolve the sitting (fresh start or resume) --------------
  useEffect(() => {
    let cancelled = false;

    async function boot() {
      setStatus('loading');
      if (!isLive) {
        let paperData;
        try {
          paperData = await interviewRuntimeAPI.getPaper(paperId);
        } catch (err) {
          if (cancelled) return;
          setStatus(statusFor(err));
          return;
        }
        if (cancelled) return;
        setPaper(paperData);
      }

      let stored = loadSittingState(userId, paperId, sessionId);
      const fromRoute = location.state || {};

      let serverRuns = null;
      if (isLive) {
        // The course page clears this interview's slot when it starts it, so
        // anything saved here is this interview in progress: a reload resumes
        // it. Opened directly, the interview is looked up on the course.
        let chosen = fromRoute.stations?.length ? fromRoute : null;
        if (!stored) {
          // With no saved sitting the server's runs decide where this one
          // stands (a second device, cleared storage). A fresh Start from the
          // course page carries its stations but still looks, because a
          // Beginnings-only account's claimed interview already has runs and a
          // new POST /runs on one is refused.
          let overview = null;
          try {
            overview = await interviewRuntimeAPI.getLeafOverview(leafKey);
          } catch (err) {
            if (cancelled) return;
            if (isUpgradeRequiredError(err) || !chosen) {
              setStatus(statusFor(err));
              return;
            }
          }
          const found = (overview?.live || []).find((i) => i.set_id === params.setId);
          if (!chosen && found) {
            chosen = {
              stations: found.stations, leaf: overview.leaf, minutes: found.minutes,
              timing: overview.timing, avatar: found.avatar, examiners: found.examiners, voices: overview.voices,
            };
          }
          serverRuns = found ? { runs: found.runs || {}, claimed: found.claimed === true } : null;
        }
        if (!stored && chosen) {
          const merged = mergeServerRuns(chosen.stations, serverRuns, Boolean(fromRoute.stations?.length));
          stored = {
            questionIds: chosen.stations.map((st, i) => (st.live ? st.question_id : `${NOTICE_PREFIX}${i}`)),
            runByQuestion: merged.runByQuestion,
            currentIndex: merged.currentIndex,
            ...(merged.complete ? { complete: true } : {}),
            stations: chosen.stations,
            leaf: chosen.leaf,
            minutes: chosen.minutes ?? null,
            timing: chosen.timing ?? null,
            avatar: chosen.avatar ?? null,
            examiner: chosen.avatar ? pickExaminer(chosen.examiners) : null,
            voice: chosen.avatar ? null : pickVoice(chosen.voices),
          };
          saveSittingState(userId, paperId, sessionId, stored);
        }
        if (!stored?.stations?.length) {
          setStatus('error');
          return;
        }
        setStations(stored.stations);
        setLiveLeaf(stored.leaf || null);
        setLiveMinutes(stored.minutes ?? null);
        setLiveTiming(stored.timing ?? null);
        setPlainVoice(stored.voice ?? null);
        // A ?avatar= on this page wins; otherwise the interview's own pin,
        // then the browser's opt-in.
        if (!new URLSearchParams(location.search).get('avatar') && AVATAR_PROVIDERS.includes(stored.avatar)) {
          setAvatarProvider(stored.avatar);
          // the interviewer belongs to the pinned provider's pool, not a ?avatar= one
          if (stored.examiner?.id && stored.examiner?.voice) setExaminer(stored.examiner);
        }
      }

      // A finished sitting stays saved until its report is opened, so a reload
      // on the closing screen doesn't lose the way to it. But the brief page's
      // "Start interview" always opens a new run and hands it over here, and
      // saved state normally wins (that's what makes a reload resume) - so a
      // retake has to be told apart from a reload: a run id the saved sitting
      // has never seen means a fresh start.
      if (stored?.complete) {
        const knownRuns = Object.values(stored.runByQuestion || {});
        const isRetake = fromRoute.runId && !knownRuns.includes(fromRoute.runId);
        if (!isRetake) {
          setQuestionIds(stored.questionIds);
          setQuestionIndex(stored.questionIds.length - 1);
          setFinishedRunIds(knownRuns.filter(Boolean));
          setSittingComplete(true);
          setStatus('ready');
          return;
        }
        clearSittingState(userId, paperId, sessionId);
        stored = null;
      }

      // currentIndex is stored explicitly, not inferred from "which
      // questions have a run_id" - a question with a run_id might be
      // finished or might be the one in progress right now, and that
      // distinction can't be told apart just by presence. The brief page
      // always starts at index 0.
      let resolvedQuestionIds;
      let resolvedRunByQuestion;
      let resolvedIndex;
      if (stored) {
        resolvedQuestionIds = stored.questionIds;
        resolvedRunByQuestion = stored.runByQuestion;
        resolvedIndex = stored.currentIndex;
      } else if (fromRoute.questionIds?.length && fromRoute.runId) {
        resolvedQuestionIds = fromRoute.questionIds;
        resolvedRunByQuestion = { [fromRoute.questionIds[0]]: fromRoute.runId };
        resolvedIndex = 0;
      } else {
        // Neither a reload nor a fresh start from the brief page - nothing
        // to resume and nothing to begin. Send the candidate back rather
        // than render an empty sitting.
        if (!cancelled) setStatus('error');
        return;
      }

      persist({
        questionIds: resolvedQuestionIds,
        runByQuestion: resolvedRunByQuestion,
        currentIndex: resolvedIndex,
      });
      setQuestionIds(resolvedQuestionIds);

      const qidAtIndex = resolvedQuestionIds[resolvedIndex];
      const existingRunId = resolvedRunByQuestion[qidAtIndex];

      if (isNoticeId(qidAtIndex)) {
        setQuestionIndex(resolvedIndex);
        setStatus('ready');
        return;
      }

      try {
        let run;
        if (existingRunId) {
          run = await interviewRuntimeAPI.getRun(existingRunId);
          if (run.is_complete) {
            // Reloaded in the narrow window between the last turn
            // finishing and the advance call completing - finish that
            // step ourselves rather than get stuck showing a done run.
            await advanceToNextQuestion(resolvedIndex, resolvedQuestionIds, resolvedRunByQuestion);
            return;
          }
        } else {
          // Defensive: the index's question should already have a run_id
          // by the time we resume at it. If not (state saved out of step
          // with reality somehow), start one rather than render nothing.
          run = await interviewRuntimeAPI.startRun(qidAtIndex);
          resolvedRunByQuestion[qidAtIndex] = run.run_id;
          persist({
            questionIds: resolvedQuestionIds, runByQuestion: resolvedRunByQuestion,
            currentIndex: resolvedIndex,
          });
        }
        if (cancelled) return;
        applyRunSnapshot(run);
        setQuestionIndex(resolvedIndex);
        setRunId(run.run_id);
        setStatus('ready');
      } catch (err) {
        if (cancelled) return;
        setStatus(statusFor(err));
      }
    }

    boot();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, paperId, sessionId]);

  // ---- live: the current station's paper -------------------------------
  // A live interview's questions come from different sets. The runtime says
  // whether a step expects a sketch or a number; what it asks for is in the
  // question's own paper, fetched per station.
  const outlineRefetchedRef = useRef('');
  useEffect(() => {
    if (!isLive || !stations) return undefined;
    const st = stations[questionIndex];
    if (!st?.paper_id) return undefined;
    const held = paper?.metadata?.interview_id === st.paper_id;
    if (held && !paper.outline_only) return undefined;
    if (held) {
      // An outline (an account with no run on this paper yet) is fetched again
      // once this station's run exists, and only once per run, so a server
      // that still answers with an outline cannot loop this effect.
      const key = `${st.paper_id}:${runId}`;
      if (!runId || outlineRefetchedRef.current === key) return undefined;
      outlineRefetchedRef.current = key;
    }
    let cancelled = false;
    interviewRuntimeAPI.getPaper(st.paper_id)
      .then((data) => { if (!cancelled) setPaper(data); })
      .catch(() => { /* the interview still runs; only a sketch prompt's wording is missing */ });
    return () => { cancelled = true; };
  }, [isLive, stations, questionIndex, paper, runId]);

  // ---- time on the current question -------------------------------------
  // Measured against a per-question target, so it counts per question: it
  // used to run across the whole sitting, which put a five-station MMI "past
  // the target time for this question" almost as soon as station two began.
  // And it waits for the Begin tap - reading the start screen isn't answering.
  useEffect(() => {
    if (status !== 'ready' || !started || sittingComplete || onNotice) return undefined;
    elapsedTickRef.current = setInterval(() => setElapsedSeconds((s) => s + 1), 1000);
    return () => clearInterval(elapsedTickRef.current);
  }, [status, started, sittingComplete, onNotice]);

  // ---- a connected face waits at most AUTO_BEGIN_SECONDS on the start screen
  useEffect(() => {
    if (!preconnect || sittingComplete) return undefined;
    const tick = setInterval(() => setAutoBeginIn((n) => Math.max(0, n - 1)), 1000);
    return () => clearInterval(tick);
  }, [preconnect, sittingComplete]);
  useEffect(() => {
    if (preconnect && autoBeginIn === 0) {
      unlockSpeech();
      setStarted(true);
    }
  }, [preconnect, autoBeginIn, unlockSpeech]);

  // ---- a long question scrolls in its own box on a wide screen: each new
  // line starts at its top, not wherever the last one was left
  useEffect(() => {
    if (captionRef.current) captionRef.current.scrollTop = 0;
    checkCaption();
  }, [say, questionIndex, started, checkCaption]);
  useEffect(() => {
    const el = captionRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    // the box (the window or the drag bar) and the question in it (typeset maths settles late)
    const watch = new ResizeObserver(checkCaption);
    watch.observe(el);
    Array.from(el.children).forEach((child) => watch.observe(child));
    return () => watch.disconnect();
  }, [status, started, checkCaption]);

  // ---- start synthesising the opening line while the start screen shows --
  // Fetching needs no tap, only playing does. Without this, "Begin interview"
  // was followed by five seconds of silence on a long opening line.
  useEffect(() => {
    if (status !== 'ready' || started || sittingComplete) return;
    const last = transcript[transcript.length - 1];
    if (last?.speaker === 'interviewer') prefetch(last.content);
  }, [status, started, sittingComplete, transcript, prefetch]);

  // ---- speak whatever the interviewer's latest line is, once ------------
  useEffect(() => {
    if (status !== 'ready' || !started || !transcript.length) return;
    if (transcript.length <= spokenTurnCountRef.current) return;
    spokenTurnCountRef.current = transcript.length;
    const last = transcript[transcript.length - 1];
    if (last.speaker === 'interviewer' && last.content) {
      speak(last.content);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transcript, status, started]);

  function applyRunSnapshot(run, { lead } = {}) {
    // start_run's response has `say` but no `transcript` field at all
    // (only get_run returns one, for the resume case) - synthesize the
    // one-entry transcript a fresh start implies, or the opening line
    // would never reach the "speak the latest interviewer turn" effect.
    const opening = run.say && lead ? `${lead}\n\n${run.say}` : run.say;
    const initialTranscript = run.transcript
      ?? (opening ? [{ speaker: 'interviewer', content: opening }] : []);
    setTranscript(initialTranscript);
    setExpects(run.expects || { sketch: false, computation: false });
    setRungId(run.rung_id ?? null);
    setQuestionExhibit(run.question_exhibit || null);
    setRungExhibit(run.rung_exhibit || null);
    setSay(opening || '');
    setSketchResult(null);
    setComputationResult(null);
    setAnswerText('');
  }

  // `spoken` is true only when the candidate's own answer just finished a
  // question. The other caller is boot, recovering a reload that landed
  // between a question finishing and the next one starting - audio isn't
  // unlocked yet there, and a "thank you" after a reload would be odd anyway.
  const advanceToNextQuestion = useCallback(async (currentIndex, qids, runByQuestion, { spoken = false } = {}) => {
    const nextIndex = currentIndex + 1;
    if (nextIndex >= qids.length) {
      persist({
        questionIds: qids, runByQuestion, currentIndex, complete: true,
      });
      setFinishedRunIds(qids.map((qid) => runByQuestion[qid]).filter(Boolean));
      setSittingComplete(true);
      if (spoken) speak(CLOSING_LINE);
      return;
    }
    const nextQid = qids[nextIndex];
    if (isNoticeId(nextQid)) {
      // A group or practical station: listed in its place, nothing to run.
      persist({ questionIds: qids, runByQuestion, currentIndex: nextIndex });
      spokenTurnCountRef.current = 0;
      applyRunSnapshot({ say: '', transcript: [] });
      setRunId(null);
      setElapsedSeconds(0);
      setQuestionIndex(nextIndex);
      return;
    }
    try {
      const run = await interviewRuntimeAPI.startRun(nextQid);
      runByQuestion[nextQid] = run.run_id;
      persist({
        questionIds: qids, runByQuestion, currentIndex: nextIndex,
      });
      spokenTurnCountRef.current = 0;
      applyRunSnapshot(run, { lead: spoken ? NEXT_QUESTION_LEAD : undefined });
      setElapsedSeconds(0);
      setQuestionIndex(nextIndex);
      setRunId(run.run_id);
    } catch (err) {
      setStatus(statusFor(err));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, paperId, sessionId, speak, persist]);

  const openFeedback = useCallback(() => {
    clearSittingState(userId, paperId, sessionId);
    navigate(`${courseUrl}/${isLive ? 'live' : paperId}/feedback`, {
      state: { runIds: finishedRunIds, mode: 'live' },
    });
  }, [userId, paperId, sessionId, courseUrl, isLive, navigate, finishedRunIds]);

  const continueFromNotice = useCallback(() => {
    const stored = loadSittingState(userId, paperId, sessionId) || { questionIds, runByQuestion: {} };
    advanceToNextQuestion(questionIndex, stored.questionIds, stored.runByQuestion || {});
  }, [userId, paperId, sessionId, questionIds, questionIndex, advanceToNextQuestion]);

  const submitAnswer = useCallback(async (text) => {
    const reply = (text ?? answerText).trim();
    if (!reply || submitting) return;
    setSubmitting(true);
    setTurnError('');
    stopSpeaking();
    try {
      const turn = await interviewRuntimeAPI.submitTurn(runId, reply);
      setTranscript((prev) => [
        ...prev,
        { speaker: 'candidate', content: reply, band: turn.band, trigger: turn.trigger },
        ...(turn.say ? [{ speaker: 'interviewer', content: turn.say }] : []),
      ]);
      setExpects(turn.expects || { sketch: false, computation: false });
      setRungId(turn.rung_id ?? null);
      setQuestionExhibit(turn.question_exhibit || null);
      setRungExhibit(turn.rung_exhibit || null);
      setSketchResult(null);
      setComputationResult(null);
      setAnswerText('');

      if (turn.is_complete) {
        const stored = loadSittingState(userId, paperId, sessionId) || { questionIds, runByQuestion: {} };
        await advanceToNextQuestion(questionIndex, stored.questionIds, stored.runByQuestion, { spoken: true });
      }
    } catch (err) {
      setTurnError(isUpgradeRequiredError(err)
        ? refusalSentence(err, 'Live interviews are on Beyond.')
        : 'Could not send that answer. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [answerText, submitting, runId, stopSpeaking, userId, paperId, sessionId, questionIds, questionIndex, advanceToNextQuestion]);

  const toggleMic = useCallback(async () => {
    if (voice.isRecording) {
      const transcribed = await voice.stop();
      if (transcribed) setAnswerText((prev) => (prev ? `${prev} ${transcribed}` : transcribed));
      return;
    }
    // Starting to answer stops the interviewer, as it would in the room - and
    // on a phone speaker, recording over the interviewer's voice could put the
    // question itself into the transcript of the answer.
    stopSpeaking();
    await voice.start();
  }, [voice, stopSpeaking]);

  // sketch/computation content for the CURRENT rung, looked up from the
  // already-fetched paper rather than round-tripped through the backend a
  // second time - the runtime's turn response only says WHETHER a rung
  // expects one (booleans, plus rung_id as a separate field), not what it
  // actually says, on purpose: that content is authored, static, and
  // already sitting in `paper`.
  const activeRungData = useMemo(() => {
    if (!paper || !rungId) return {};
    const question = (paper.questions || []).find(
      (q) => q.external_question_id === questionIds[questionIndex],
    );
    const rung = (question?.rungs || []).find((r) => r.rung_id === rungId);
    return { sketch: rung?.sketch, computation: rung?.computation };
  }, [paper, questionIds, questionIndex, rungId]);

  const submitSketch = useCallback(async (dataUrl) => {
    setSketchBusy(true);
    try {
      const result = await interviewRuntimeAPI.checkSketch(runId, dataUrl);
      setSketchResult(result);
    } catch {
      setSketchResult(null);
    } finally {
      setSketchBusy(false);
    }
  }, [runId]);

  const submitComputation = useCallback(async (value) => {
    setComputationBusy(true);
    try {
      const result = await interviewRuntimeAPI.checkComputation(runId, value);
      setComputationResult(result);
    } catch {
      setComputationResult({ correct: false });
    } finally {
      setComputationBusy(false);
    }
  }, [runId]);

  if (status === 'loading') {
    return <div className="is-loading">Loading your interview…</div>;
  }
  if (status === 'upgrade') {
    return <div className="is-loading"><InterviewUpgradeGate code={gateCode} paid={paid} /></div>;
  }
  if (status === 'error') {
    return (
      <div className="is-loading">
        Could not load this interview. Go back to{' '}
        <a href={withAppBasename(isLive ? courseUrl : `${courseUrl}/${paperId}`)}>{isLive ? 'the course page' : 'the brief'}</a>.
      </div>
    );
  }
  if (sittingComplete) {
    return (
      <div className="is-loading">
        <h1 className="is-start-title">That is the end of this interview</h1>
        <p className="is-start-note">
          Your feedback has a mark for each answer and a report on the whole interview.
          You can then see model answers for every question.
        </p>
        <button type="button" className="is-btn is-submit-btn" onClick={openFeedback}>
          See your feedback
        </button>
      </div>
    );
  }
  const liveCount = questionIds.filter((qid) => !isNoticeId(qid)).length;
  const isOxbridgeLive = isLive && liveLeaf?.workspace === 'Oxbridge';
  const sittingTitle = isLive
    ? `${liveLeaf?.display_name || 'Live'} interview`
    : (paper?.metadata?.title || 'Your interview');
  let startSummary;
  if (isLive && !isOxbridgeLive) {
    startSummary = `${questionIds.length} station${questionIds.length === 1 ? '' : 's'}.`;
  } else {
    startSummary = `${liveCount} question${liveCount === 1 ? '' : 's'}.`;
  }
  const unit = isLive && !isOxbridgeLive ? 'Station' : 'Question';

  if (!started) {
    // Phones (iOS Safari especially) only allow audio to play after a real
    // tap has happened on the page - unlockSpeech() runs synchronously
    // right here, in the tap itself, so the interviewer's opening line
    // (and everything after it) is actually audible rather than silent.
    return (
      <div className="is-loading">
        <h1 className="is-start-title">{sittingTitle}</h1>
        <p className="is-start-note">
          {startSummary}
          {' '}The examiner asks each question aloud and listens to your answer. What they ask next
          depends on what you said: they can push you further when you get it right, give you a hint
          when you are stuck, or pick up a common mistake.
        </p>
        <p className="is-start-note">
          Some questions have a graph, diagram or text to work from, and there is a whiteboard for
          sketches and working. At the end you get a mark for each answer, a report on the whole
          interview, and model answers.
        </p>
        {isLive && liveTiming && (
          <p className="is-start-note">{timingText(liveTiming, liveLeaf?.institution)}</p>
        )}
        <p className="is-start-note">
          Turn your sound on. The examiner speaks first. Record or type your answer, check it, then submit.
        </p>
        {preconnect && (
          <p className="is-start-note is-start-countdown">
            The interview begins by itself in {autoBeginIn} second{autoBeginIn === 1 ? '' : 's'}.
          </p>
        )}
        <button
          type="button"
          className="is-btn is-submit-btn"
          onClick={() => { unlockSpeech(); setStarted(true); }}
        >
          Begin interview
        </button>
      </div>
    );
  }

  const institution = isLive ? liveLeaf?.institution : paper?.metadata?.institution;
  const examinerName = institution ? `${institution} examiner` : 'Interviewer';

  if (onNotice) {
    const station = stations?.[questionIndex];
    return (
      <div className="is-loading">
        <h1 className="is-start-title">
          {`${unit} ${questionIndex + 1}: ${station?.label || 'Group or practical task'}`}
        </h1>
        <p className="is-start-note">
          This is a group or practical task, so it is not run here. In the real interview it comes at this point.
        </p>
        <button type="button" className="is-btn is-submit-btn" onClick={continueFromNotice}>
          Continue
        </button>
      </div>
    );
  }
  const lastInterviewerLine = [...transcript].reverse().find((t) => t.speaker === 'interviewer');
  const elapsedLabel = `${String(Math.floor(elapsedSeconds / 60)).padStart(2, '0')}:${String(elapsedSeconds % 60).padStart(2, '0')}`;
  const overTarget = softTargetSeconds ? elapsedSeconds > softTargetSeconds : false;

  return (
    <div className="is-shell">
      <div className="is-bar">
        <span className="is-bar-title">
          {isLive ? `${sittingTitle} · ${unit} ${questionIndex + 1} of ${questionIds.length}` : paper?.metadata?.title}
        </span>
        <div className="is-progress">
          {questionIds.map((qid, i) => (
            <span
              key={qid}
              className={`is-pip${i < questionIndex ? ' is-pip--done' : i === questionIndex ? ' is-pip--now' : ''}`}
            />
          ))}
        </div>
        {softTargetSeconds ? (
          <span className={`is-timer${overTarget ? ' is-timer--over' : ''}`}>{elapsedLabel}</span>
        ) : <span />}
      </div>
      {overTarget && (
        <div className="is-timer-warning">
          You are past the time for this question. Finish your answer when you are ready.
        </div>
      )}

      <div className="is-body">
        <div
          className={`is-left${stageHeight != null ? ' is-left--sized' : ''}`}
          ref={leftRef}
          style={stageHeight != null ? { '--stage-h': `${stageHeight}px` } : undefined}
        >
          <InterviewAvatarStage
            examinerName={examinerName}
            examinerRole="Examiner"
            isSpeaking={isSpeaking}
            getAmplitude={getAmplitude}
            avatar={avatarProvider ? avatar : null}
            faceHeld={speakingPlainly}
            cameraEnabled={cameraEnabled}
            onToggleCamera={() => setCameraEnabled((v) => !v)}
          />
          <StageDivider columnRef={leftRef} height={stageHeight} onResize={resizeStage} />

          <div className="is-caption" ref={captionRef} onScroll={checkCaption}>
            <div className="is-caption-who">Interviewer</div>
            {/* The line as written, maths typeset, not the flattened text the
                voice is given (promptText.js). */}
            <PromptLine
              className="is-caption-text"
              question={(paper?.questions || []).find((q) => q.external_question_id === questionIds[questionIndex])}
              said={lastInterviewerLine?.content || say}
            />
            {captionMore && <div className="is-caption-more" aria-hidden="true">More of the question below</div>}
          </div>

          <div className="is-answer">
            {turnError && <div className="is-answer-error">{turnError}</div>}
            <textarea
              className="is-answer-box"
              rows={3}
              value={answerText}
              onChange={(e) => setAnswerText(e.target.value)}
              placeholder="Record your answer or type it here. You can edit it before you submit."
              disabled={submitting}
            />
            {voice.error && <div className="is-answer-hint">{voice.error}</div>}
            <div className="is-answer-foot">
              <button
                type="button"
                className={`is-ctrl${voice.isRecording ? ' is-ctrl--on' : ''}`}
                onClick={toggleMic}
                disabled={submitting || voice.isTranscribing}
              >
                {voice.isTranscribing ? 'Transcribing…' : voice.isRecording ? 'Stop recording' : 'Record answer'}
              </button>
              <button
                type="button"
                className="is-btn is-submit-btn"
                onClick={() => submitAnswer()}
                disabled={submitting || !answerText.trim()}
              >
                {submitting ? 'Sending…' : 'Submit answer'}
              </button>
            </div>
          </div>
        </div>

        <div className="is-right">
          <InterviewBoard
            key={questionIds[questionIndex]}
            questionExhibit={questionExhibit}
            rungExhibit={rungExhibit}
            expects={expects}
            sketchPrompt={activeRungData.sketch?.what}
            computationUnits={activeRungData.computation?.units}
            onSubmitSketch={submitSketch}
            onSubmitComputation={submitComputation}
            sketchBusy={sketchBusy}
            sketchResult={sketchResult}
            computationBusy={computationBusy}
            computationResult={computationResult}
          />
        </div>
      </div>
    </div>
  );
}
